import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Shield, FileText, Lock, AlertCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export const LegalCompliance = () => {
  const faqs = [
    {
      question: "Are you a law firm?",
      answer: "No. ActionFraudOne is not a law firm. We are a legal concierge and coordination service. We do not provide legal advice, representation, or regulated legal services. Our role is to assess your legal matter, identify the correct legal professional for your situation, and coordinate the introduction."
    },
    {
      question: "How do you choose which solicitor or legal professional to refer me to?",
      answer: "We have an established network of trusted, verified legal partners across the UK and worldwide. When you contact us, we assess your case, identify the legal area and complexity, and match you with the professional who is genuinely qualified to handle your matter."
    },
    {
      question: "Do you charge a fee for your service?",
      answer: "Yes. We charge a fixed coordination fee for our case assessment, specialist matching, and coordination services. The initial consultation is complimentary. Legal professionals we refer you to will bill separately for their services."
    },
    {
      question: "What if my case involves international law or I need representation abroad?",
      answer: "We specialize in cross-border and international matters. Our global network includes verified legal partners across multiple jurisdictions. We can connect you to qualified professionals worldwide, including for urgent matters like detention abroad."
    },
    {
      question: "Can you help with urgent cases?",
      answer: "Yes. Urgent cases are prioritised immediately. Most clients receive same-day contact. For critical situations like someone detained abroad, we coordinate emergency representation through our international partners."
    },
    {
      question: "Is my information kept confidential?",
      answer: "Absolutely. All consultations and communications are handled with complete confidentiality. We also offer anonymous legal services where your identity is protected throughout the entire process."
    },
    {
      question: "What types of legal matters do you handle?",
      answer: "We coordinate across all areas of law: due diligence, property conveyancing, commodity contracts, fraud recovery, HMRC/tax issues, representation abroad, company formations, private investigations, and more. If it's a legal matter, we know who to turn to."
    },
    {
      question: "Do you offer 'no win, no fee' services?",
      answer: "Yes. For fraud recovery and certain claim types, we work with partners who operate on a no win, no fee basis. You only pay if funds are successfully recovered."
    },
    {
      question: "How quickly will I hear back after contacting you?",
      answer: "Most clients receive same-day contact. For urgent matters, we respond within hours. We understand that legal issues are time-sensitive and prioritise accordingly."
    },
    {
      question: "What if I'm not sure what type of legal help I need?",
      answer: "That's exactly what we're here for. Many clients contact us unsure of the legal route required. We assess your situation, identify the correct legal category, and direct you to the right specialist—no confusion, no wasted time."
    }
  ];

  return (
    <div className="min-h-screen pt-32 pb-24">
      {/* Hero Section */}
      <section className="py-16 bg-black relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-72 h-72 border border-gold/10 transform rotate-45"></div>
          <div className="absolute bottom-20 left-20 w-56 h-56 border border-gold/10 transform -rotate-12"></div>
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <span className="text-gold text-sm tracking-widest uppercase font-semibold">Legal Information</span>
            <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight">
              Legal & Compliance
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Important information about our services, regulatory framework, and your rights.
            </p>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 bg-dark-black">
        <div className="container mx-auto px-4 lg:px-8">
          <Card className="max-w-5xl mx-auto bg-gold/10 border-gold">
            <CardContent className="p-8">
              <div className="flex items-start space-x-4">
                <AlertCircle className="text-gold flex-shrink-0 mt-1" size={24} />
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-white">Important Notice</h3>
                  <p className="text-gray-300 leading-relaxed">
                    ActionFraudOne is not a law firm. We do not provide legal advice, representation, or services that are reserved for regulated legal professionals. We act as a legal concierge and coordination service, connecting clients to verified solicitors and legal specialists who are appropriately qualified and regulated.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-gold text-sm tracking-widest uppercase font-semibold">FAQ</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">Frequently Asked Questions</h2>
              <p className="text-gray-300 text-lg">Everything you need to know about our services</p>
            </div>

            <Card className="bg-dark-black border-gold/20">
              <CardContent className="p-8">
                <Accordion type="single" collapsible className="w-full space-y-4">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`} className="border-gold/20">
                      <AccordionTrigger className="text-white hover:text-gold text-left font-semibold">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-300 leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Terms & Conditions */}
      <section className="py-24 bg-dark-black">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-5xl mx-auto space-y-12">
            <Card className="bg-black border-gold/20">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gold/10 border border-gold flex items-center justify-center flex-shrink-0">
                    <FileText className="text-gold" size={24} />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white mb-4">Terms & Conditions</h2>
                  </div>
                </div>
                <div className="space-y-6 text-gray-300 leading-relaxed">
                  <div>
                    <h4 className="text-white font-bold text-lg mb-2">1. Nature of Services</h4>
                    <p>
                      ActionFraudOne provides legal concierge and coordination services. We are not a law firm and do not provide legal advice, representation, or regulated legal services. Our role is to assess your legal matter, connect you to appropriate legal professionals, and coordinate the introduction.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-white font-bold text-lg mb-2">2. Client Responsibilities</h4>
                    <p>
                      Clients are responsible for providing accurate information, responding to requests in a timely manner, and ensuring payment of agreed fees. Clients engage directly with referred legal professionals for substantive legal work.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-white font-bold text-lg mb-2">3. Fees and Payment</h4>
                    <p>
                      We charge fixed coordination fees as agreed in advance. The initial consultation is complimentary. Legal professionals we refer you to will bill separately for their services. Payment terms are typically 14 days from invoice date unless otherwise agreed.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-white font-bold text-lg mb-2">4. Confidentiality</h4>
                    <p>
                      We maintain strict confidentiality regarding all client matters and information, subject to legal and regulatory obligations to disclose. For sensitive matters, we offer anonymous coordination services.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-white font-bold text-lg mb-2">5. Limitation of Liability</h4>
                    <p>
                      Our liability is limited to the coordination and referral services we provide. We are not liable for the acts or omissions of legal professionals we refer you to. We maintain appropriate professional indemnity insurance.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-white font-bold text-lg mb-2">6. Termination</h4>
                    <p>
                      Either party may terminate our engagement with reasonable written notice. Fees for services completed up to the date of termination remain payable.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Privacy Notice */}
            <Card className="bg-black border-gold/20">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gold/10 border border-gold flex items-center justify-center flex-shrink-0">
                    <Lock className="text-gold" size={24} />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white mb-4">Privacy Notice</h2>
                  </div>
                </div>
                <div className="space-y-6 text-gray-300 leading-relaxed">
                  <p>
                    We are committed to protecting your privacy and handling your personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
                  </p>

                  <div>
                    <h4 className="text-white font-bold mb-3">How We Use Your Information</h4>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>To provide coordination services and respond to your enquiries</li>
                      <li>To assess your legal matter and identify appropriate specialists</li>
                      <li>To coordinate with legal professionals on your behalf</li>
                      <li>To maintain records of our engagements and communications</li>
                      <li>To comply with legal and regulatory obligations</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-white font-bold mb-3">Your Rights</h4>
                    <p>
                      You have the right to access, correct, delete, or restrict the processing of your personal data. You also have the right to object to processing and to data portability. To exercise these rights, please contact us at info@actionfraudone.com.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-white font-bold mb-3">Data Security</h4>
                    <p>
                      We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Regulatory Framework */}
            <Card className="bg-black border-gold/20">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gold/10 border border-gold flex items-center justify-center flex-shrink-0">
                    <Shield className="text-gold" size={24} />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white mb-4">Regulatory Framework</h2>
                  </div>
                </div>
                <div className="space-y-4 text-gray-300 leading-relaxed">
                  <p>
                    ActionFraudOne is registered in England and Wales. Our coordination services are designed to complement, not replace, the expertise of regulated legal professionals.
                  </p>
                  <p>
                    Where regulated legal services are required, we coordinate with solicitors who are authorized and regulated by the Solicitors Regulation Authority (SRA) or other appropriate regulatory bodies in their respective jurisdictions.
                  </p>
                  <p>
                    We maintain professional indemnity insurance appropriate to our services and operate in accordance with all applicable UK laws and regulations governing consultancy services.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contact for Legal Matters */}
            <Card className="bg-gradient-to-br from-gold/10 to-transparent border-gold">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Questions About Our Legal Framework?</h2>
                <p className="text-gray-300 mb-6">
                  If you have questions about our regulatory status, terms of service, or compliance framework, please contact us.
                </p>
                <div className="space-y-2">
                  <p className="text-white"><strong>Email:</strong> <span className="text-gray-300">info@actionfraudone.com</span></p>
                  <p className="text-white"><strong>Telephone:</strong> <a href="tel:+447386668966" className="text-gray-300 hover:text-gold">+44 7386 668966</a></p>
                  <p className="text-white"><strong>Address:</strong> <span className="text-gray-300">167-169 Great Portland Street, 5th Floor, London, England, W1W 5PF</span></p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Last Updated */}
      <section className="py-8 bg-black border-t border-gold/20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <p className="text-gray-500 text-sm text-center">
              Last updated: January 2025
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
